import { rootApi } from '../rootApi';

// Helper function to normalize string filters (lowercase and remove spaces)
const normalizeStringFilter = (value) => value?.toLowerCase().replace(/\s+/g, '');

// Helper function to add filter params to body
const addFilterParams = (body, params) => {
    const filterConfig = {
        // ID filters - add as-is if truthy
        dealerId: (value) => value,
        addressId: (value) => value,

        // String filters - normalize before adding
        country: normalizeStringFilter,
        addressName: normalizeStringFilter,

        // Date filters - add as-is if truthy
        contractStartDate: (value) => value,
        contractEndDate: (value) => value
    };

    Object.entries(filterConfig).forEach(([key, transformer]) => {
        const value = params[key];
        if (value) {
            body[key] = transformer(value);
        }
    });

    return body;
};

export const dealerAddressApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getAllDealerAddress: builder.query({
            query: ({ pageNumber, pageSize, sortBy, sortDir, ...filterParams }) => {
                const body = {
                    PageNumber: pageNumber !== undefined ? pageNumber : 1,
                    PageSize: pageSize || 10,
                    sortBy,
                    sortDir
                };

                // Add all filter parameters using the helper function
                addFilterParams(body, filterParams);

                return {
                    url: 'v1/dealeraddress/getlist',
                    method: 'POST',
                    body
                };
            },
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data.items)) {
                        return response.data;
                    }
                    if (Array.isArray(response.items)) {
                        return response;
                    }
                }
                return { items: [], totalCount: 0 };
            },
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch (error) {
                    console.error('Dealer address query failed', error);
                }
            },
            providesTags: ['DealerAddress']
        }),

        createDealerAddress: builder.mutation({
            query: (data) => ({
                url: 'v1/dealeraddress/create',
                method: 'POST',
                body: data,
                headers: {
                    'Content-Type': 'application/json'
                }
            }),
            invalidatesTags: ['DealerAddress']
        }),

        updateDealerAddress: builder.mutation({
            query: (data) => ({
                url: 'v1/dealeraddress/update',
                method: 'POST',
                body: data,
                headers: {
                    'Content-Type': 'application/json'
                }
            }),
            invalidatesTags: ['DealerAddress']
        }),

        deleteDealerAddress: builder.mutation({
            query: (data) => ({
                url: 'v1/dealeraddress/delete',
                method: 'DELETE',
                body: data,
                headers: {
                    'Content-Type': 'application/json'
                }
            }),
            invalidatesTags: ['DealerAddress']
        }),

        getByDealerId: builder.query({
            query: (id) => `v1/dealeraddress/getbydealerid/${id}`,
            providesTags: (result, error, id) => [{ type: 'getByDealerId', id }]
        }),

        getDealerAddressByDealerId: builder.query({
            query: (dealerId) => ({
                url: 'v1/dealeraddress/getbydealerid',
                method: 'POST',
                body: { dealerId },
                headers: {
                    'Content-Type': 'application/json'
                }
            }),
            transformResponse: (response) => response?.data || { items: [] },
            providesTags: (result, error, dealerId) => [{ type: 'DealerAddress', id: dealerId }],
            keepUnusedDataFor: 60
        }),

        getDealerAddressBySap: builder.query({
            query: ({ dealerId, languageId }) => ({
                url: 'v1/dealeraddress/getdealeradressbysap',
                method: 'POST',
                body: { dealerId, languageId },
                headers: {
                    'Content-Type': 'application/json'
                }
            }),
            transformResponse: (response) => response?.data || { items: [] },
            providesTags: (result, error, dealerId) => [{ type: 'DealerAddress', id: dealerId }],
            keepUnusedDataFor: 60
        })
    })
});

export const { useGetAllDealerAddressQuery, useCreateDealerAddressMutation, useUpdateDealerAddressMutation, useDeleteDealerAddressMutation, useGetByDealerIdQuery, useGetDealerAddressByDealerIdQuery, useGetDealerAddressBySapQuery } = dealerAddressApi;
